<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import { useBillingCheckout } from 'dashboard/composables/useBillingCheckout';

const props = defineProps({
  agentsLimit: { type: Number, required: true },
  inboxesLimit: { type: Number, required: true },
  agentsBaseLimit: { type: Number, default: 0 },
  inboxesBaseLimit: { type: Number, default: 0 },
  agentsUsed: { type: Number, required: true },
  inboxesUsed: { type: Number, required: true },
  agentsMinimumLimit: { type: Number, default: null },
  inboxesMinimumLimit: { type: Number, default: null },
  pendingAgents: { type: Number, default: null },
  pendingInboxes: { type: Number, default: null },
  disabled: { type: Boolean, default: false },
});

const emit = defineEmits(['updated']);

const { t } = useI18n();
const { isPaying, reduceSeats } = useBillingCheckout();

const dialogRef = ref(null);
const targetAgents = ref(props.agentsLimit);
const targetInboxes = ref(props.inboxesLimit);

const agentsReduceMin = computed(
  () =>
    props.agentsMinimumLimit ??
    Math.max(props.agentsUsed, props.agentsBaseLimit)
);

const inboxesReduceMin = computed(
  () =>
    props.inboxesMinimumLimit ??
    Math.max(props.inboxesUsed, props.inboxesBaseLimit)
);

const canReduceAgents = computed(
  () => props.agentsLimit > props.agentsBaseLimit
);

const canReduceInboxes = computed(
  () => props.inboxesLimit > props.inboxesBaseLimit
);

const syncTargets = () => {
  targetAgents.value = props.agentsLimit;
  targetInboxes.value = props.inboxesLimit;
};

watch(
  () => [props.agentsLimit, props.inboxesLimit],
  () => {
    syncTargets();
  }
);

const openModal = () => {
  if (props.disabled) return;

  syncTargets();
  dialogRef.value?.open();
};

const onReduceAgents = async () => {
  const limit = Number(targetAgents.value);
  if (!Number.isFinite(limit)) return;
  const ok = await reduceSeats({ limitKey: 'agents', newLimit: limit });
  if (ok) {
    dialogRef.value?.close();
    emit('updated');
  }
};

const onReduceInboxes = async () => {
  const limit = Number(targetInboxes.value);
  if (!Number.isFinite(limit)) return;
  const ok = await reduceSeats({ limitKey: 'inboxes', newLimit: limit });
  if (ok) {
    dialogRef.value?.close();
    emit('updated');
  }
};

defineExpose({ open: openModal });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    :title="t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.SEATS_REDUCE_TITLE')"
    :description="t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.SEAT_REDUCE_NOTE')"
    :show-confirm-button="false"
    show-cancel-button
    width="md"
  >
    <p v-if="pendingAgents != null" class="mb-0 text-sm text-n-amber-11">
      {{
        t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.SEAT_PENDING_AGENTS', {
          count: pendingAgents,
        })
      }}
    </p>
    <p v-if="pendingInboxes != null" class="mb-0 text-sm text-n-amber-11">
      {{
        t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.SEAT_PENDING_INBOXES', {
          count: pendingInboxes,
        })
      }}
    </p>

    <div class="grid gap-4 sm:grid-cols-2">
      <div
        class="flex flex-col gap-2 rounded-lg border border-solid border-n-weak p-4"
      >
        <p class="mb-0 text-sm font-medium text-n-slate-12">
          {{ t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.AGENTS_USAGE_LABEL') }}
          ({{ agentsUsed }} / {{ agentsLimit }})
        </p>
        <p class="mb-0 text-xs text-n-slate-11">
          {{
            t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.SEAT_INCLUDED_IN_PLAN', {
              count: agentsBaseLimit,
            })
          }}
        </p>
        <p class="mb-0 text-xs text-n-slate-11">
          {{
            t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.SEAT_REDUCE_MIN_HELPER', {
              usage: agentsUsed,
              min: agentsReduceMin,
            })
          }}
        </p>
        <Input
          v-model.number="targetAgents"
          type="number"
          size="sm"
          :min="agentsReduceMin"
          :max="agentsLimit - 1"
          :label="t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.SEAT_REDUCE_TARGET')"
          :disabled="isPaying || !canReduceAgents"
        />
        <Button
          size="sm"
          color="slate"
          variant="faded"
          class="w-fit"
          :label="t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.SEAT_REDUCE_SUBMIT')"
          :disabled="
            isPaying ||
            !canReduceAgents ||
            targetAgents >= agentsLimit ||
            targetAgents < agentsReduceMin
          "
          @click="onReduceAgents"
        />
      </div>

      <div
        class="flex flex-col gap-2 rounded-lg border border-solid border-n-weak p-4"
      >
        <p class="mb-0 text-sm font-medium text-n-slate-12">
          {{ t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.INBOXES_USAGE_LABEL') }}
          ({{ inboxesUsed }} / {{ inboxesLimit }})
        </p>
        <p class="mb-0 text-xs text-n-slate-11">
          {{
            t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.SEAT_INCLUDED_IN_PLAN', {
              count: inboxesBaseLimit,
            })
          }}
        </p>
        <p class="mb-0 text-xs text-n-slate-11">
          {{
            t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.SEAT_REDUCE_MIN_HELPER', {
              usage: inboxesUsed,
              min: inboxesReduceMin,
            })
          }}
        </p>
        <Input
          v-model.number="targetInboxes"
          type="number"
          size="sm"
          :min="inboxesReduceMin"
          :max="inboxesLimit - 1"
          :label="t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.SEAT_REDUCE_TARGET')"
          :disabled="isPaying || !canReduceInboxes"
        />
        <Button
          size="sm"
          color="slate"
          variant="faded"
          class="w-fit"
          :label="t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.SEAT_REDUCE_SUBMIT')"
          :disabled="
            isPaying ||
            !canReduceInboxes ||
            targetInboxes >= inboxesLimit ||
            targetInboxes < inboxesReduceMin
          "
          @click="onReduceInboxes"
        />
      </div>
    </div>
  </Dialog>
</template>
